import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO, { createBreadcrumbSchema, createFAQSchema, softwareApplicationSchema } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle, Zap, Image, TrendingUp, Palette, Target, Clock } from 'lucide-react';

const AIImageGeneratorPinterest = () => {
  const faqs = [
    {
      question: "What size images work best for Pinterest?",
      answer: "Pinterest recommends a 2:3 aspect ratio (1000x1500px) for optimal display. Our AI generator creates perfectly sized Pinterest pins that look great on both mobile and desktop."
    },
    {
      question: "Can I create multiple Pinterest pins at once?",
      answer: "Yes! Our bulk generation feature lets you create hundreds of unique Pinterest pins in minutes, each with variations in style, color, and composition."
    },
    {
      question: "Are the AI-generated images suitable for commercial Pinterest use?",
      answer: "Absolutely. All images generated with our platform come with full commercial rights, making them perfect for Pinterest marketing, affiliate content, and business promotion."
    },
    {
      question: "How do I create click-worthy Pinterest images?",
      answer: "Use our AI to generate eye-catching visuals with bold colors, clear text overlays, and lifestyle imagery. Our FLUX model excels at creating scroll-stopping Pinterest content."
    }
  ];

  const features = [
    {
      icon: <Image className="w-6 h-6" />,
      title: "Pinterest-Optimized Sizes",
      description: "Auto-generate images in perfect 2:3 Pinterest ratio (1000x1500px) for maximum engagement"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Bulk Pin Generation",
      description: "Create 100+ unique Pinterest pins in minutes with AI-powered variations"
    },
    {
      icon: <Palette className="w-6 h-6" />,
      title: "Trending Aesthetics",
      description: "Generate pins matching Pinterest's trending styles: minimalist, boho, modern, vintage"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Niche-Specific Content",
      description: "AI trained for top Pinterest niches: recipes, DIY, fashion, home decor, wellness"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Click-Worthy Designs",
      description: "Create pins designed to drive traffic with compelling visuals and clear messaging"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Save 20+ Hours/Week",
      description: "Automate your Pinterest content creation and focus on strategy and engagement"
    }
  ];

  const useCases = [
    "Recipe and food bloggers creating visual pins",
    "Affiliate marketers generating product showcase pins",
    "Interior designers showcasing room concepts",
    "Fashion bloggers creating outfit inspiration boards",
    "DIY and craft content creators",
    "Business coaches creating quote and tip graphics",
    "Travel bloggers generating destination imagery",
    "Wedding planners creating inspiration content"
  ];

  return (
    <>
      <SEO
        title="AI Pinterest Image Generator | Create Viral Pins That Drive Traffic"
        description="Generate stunning Pinterest pins with AI. Create click-worthy images in the perfect 2:3 ratio. Bulk generate 100+ pins in minutes. Perfect for bloggers, marketers & content creators."
        keywords="pinterest image generator ai, pinterest pin maker, ai pinterest pins, bulk pinterest images, pinterest content creator, pinterest marketing tool, ai pin generator, pinterest graphic maker"
        url="/ai-image-generator-pinterest"
        jsonLd={[
          softwareApplicationSchema,
          createBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'AI Pinterest Image Generator', url: '/ai-image-generator-pinterest' }
          ]),
          createFAQSchema(faqs)
        ]}
      />
      
      <Header />
      
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 via-red-500/5 to-background" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 bg-pink-500/10 text-pink-500 border-pink-500/20">
                Pinterest-Optimized AI Images
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                AI Pinterest Image Generator
                <span className="block text-pink-500">Create Viral Pins That Drive Traffic</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Generate scroll-stopping Pinterest pins with AI. Perfect 2:3 ratio, trending aesthetics, 
                bulk creation. Turn your Pinterest into a traffic machine.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-pink-500 hover:bg-pink-600">
                  <Link to="/try-generator">
                    Create Pinterest Pins Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/gallery">View Pinterest Examples</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Why Pinterest Creators Choose Our AI Generator
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <Card key={index} className="border-pink-500/10 hover:border-pink-500/30 transition-colors">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-pink-500/10 flex items-center justify-center text-pink-500 mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                Perfect For Pinterest Content Creators
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {useCases.map((useCase, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
                    <CheckCircle className="w-5 h-5 text-pink-500 flex-shrink-0" />
                    <span>{useCase}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Start Creating Viral Pinterest Pins Today
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Join 10,000+ Pinterest creators using AI to scale their content. 
                No design skills needed.
              </p>
              <Button asChild size="lg" className="bg-pink-500 hover:bg-pink-600">
                <Link to="/try-generator">
                  Generate Pinterest Pins Free <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default AIImageGeneratorPinterest;
